/*
 * cx-squad-canvas.js -- Bride Squad / Groom Squad wedding patch compositor.
 *
 * ONE renderer for everything: the on-page preview grid, the flattened PNG
 * that rides to the cart as the `Custom Image` property, and the product
 * cover / sample images (rendered from this same file in a headless
 * browser), so what the customer sees is exactly what gets printed.
 *
 * Design: a festive Indian-wedding "card" badge -- concave-corner frame
 * with a double gold border, marigold flowers on the crest and corners,
 * the squad phrase as a two-font lockup (Great Vibes script + Cinzel
 * caps), a gold divider, and the person's name in Cinzel Bold.
 * Everything outside the frame is transparent (clear DTF film).
 *
 * Fonts (all SIL OFL, vendored as woff2 with the cx-squad- prefix):
 *   cx_squad_script  -> Great Vibes
 *   cx_squad_caps    -> Cinzel Bold (instanced at wght 700)
 *
 * Public API (window.CXSquad):
 *   PHRASES          { bride: [...], groom: [...] }   squad phrases
 *   COUPLE_TITLES    { bride: [...], groom: [...] }   couple patch titles
 *   COLOURWAYS       [{ id, label, swatch }]
 *   ASPECT           height / width of every patch (constant)
 *   ready()          Promise, resolves once both fonts are usable
 *   render(canvas, opts)
 *      opts.lockup    a phrase/title object from PHRASES / COUPLE_TITLES
 *      opts.name      string ('' allowed only when opts.special)
 *      opts.colourway colourway id
 *      opts.special   true -> couple patch layout (title is the hero)
 *      opts.widthPx   output width in px (height = widthPx * ASPECT)
 *      opts.placeholder  true -> draw the name faded (empty-state sample)
 */
(function () {
  'use strict';

  var SCRIPT = 'cx_squad_script';
  var CAPS = 'cx_squad_caps';
  var BASE_W = 1000;
  var ASPECT = 0.72;
  var BASE_H = BASE_W * ASPECT;

  // Each phrase is a lockup of two lines: a Cinzel caps line ("caps") and
  // a Great Vibes script line ("script"), stacked in the order given.
  function L(label, lines) {
    return { label: label, lines: lines };
  }
  var PHRASES = {
    bride: [
      L('Bride Squad', [{ t: 'Bride', s: 'script' }, { t: 'SQUAD', s: 'caps' }]),
      L('Team Bride', [{ t: 'TEAM', s: 'caps' }, { t: 'Bride', s: 'script' }]),
      L("Bride's Gang", [{ t: 'Bride’s', s: 'script' }, { t: 'GANG', s: 'caps' }]),
      L('Dulhan Ki Saheliyan', [{ t: 'DULHAN KI', s: 'caps' }, { t: 'Saheliyan', s: 'script' }]),
      L('Bride Tribe', [{ t: 'Bride', s: 'script' }, { t: 'TRIBE', s: 'caps' }]),
    ],
    groom: [
      L('Groom Squad', [{ t: 'Groom', s: 'script' }, { t: 'SQUAD', s: 'caps' }]),
      L('Dulhe Ki Toli', [{ t: 'DULHE KI', s: 'caps' }, { t: 'Toli', s: 'script' }]),
      L("Groom's Crew", [{ t: 'Groom’s', s: 'script' }, { t: 'CREW', s: 'caps' }]),
      L('Team Groom', [{ t: 'TEAM', s: 'caps' }, { t: 'Groom', s: 'script' }]),
      L('Baraati Gang', [{ t: 'Baraati', s: 'script' }, { t: 'GANG', s: 'caps' }]),
    ],
  };
  var COUPLE_TITLES = {
    bride: [
      L('The Bride', [{ t: 'THE', s: 'caps' }, { t: 'Bride', s: 'script' }]),
      L('Bride to Be', [{ t: 'Bride', s: 'script' }, { t: 'TO BE', s: 'caps' }]),
      L('Dulhan', [{ t: 'Dulhan', s: 'script' }]),
    ],
    groom: [
      L('The Groom', [{ t: 'THE', s: 'caps' }, { t: 'Groom', s: 'script' }]),
      L('Dulha', [{ t: 'Dulha', s: 'script' }]),
      L('Groom to Be', [{ t: 'Groom', s: 'script' }, { t: 'TO BE', s: 'caps' }]),
    ],
  };

  var GOLD = ['#FCE9A8', '#EDC25A', '#C8902C', '#E9B949', '#FBE3A0'];
  var MARIGOLD = { petals: ['#F08C00', '#F59F00', '#FCC419'], centre: '#B4530A', line: 'rgba(90,30,0,0.28)' };
  var MARIGOLD_RED = { petals: ['#D9480F', '#E8590C', '#F76707'], centre: '#7A1426', line: 'rgba(70,10,10,0.3)' };
  var LEAF = ['#2B8A3E', '#1E6B2F'];

  var PALETTES = {
    maroon: {
      label: 'Maroon & Gold',
      swatch: 'linear-gradient(135deg,#9A1F36 0 55%,#E3B24F 55% 100%)',
      bg: ['#A3243B', '#6A0E21'],
      frame: GOLD,
      text: GOLD,
      shadow: 'rgba(30,0,5,0.45)',
      marigold: MARIGOLD,
    },
    pink: {
      label: 'Rani Pink & Gold',
      swatch: 'linear-gradient(135deg,#D6336C 0 55%,#E3B24F 55% 100%)',
      bg: ['#D93A7B', '#930E45'],
      frame: GOLD,
      text: GOLD,
      shadow: 'rgba(60,0,25,0.45)',
      marigold: MARIGOLD,
    },
    haldi: {
      label: 'Haldi Yellow',
      swatch: 'linear-gradient(135deg,#FFC929 0 55%,#8E1B2E 55% 100%)',
      bg: ['#FFDA4D', '#F5A800'],
      frame: ['#9B2335', '#7A1426', '#5C0A18', '#7A1426', '#9B2335'],
      text: ['#A12A3C', '#86182B', '#5C0A18', '#7A1426', '#9B2335'],
      shadow: 'rgba(120,60,0,0.25)',
      marigold: MARIGOLD_RED,
    },
  };
  var COLOURWAYS = Object.keys(PALETTES).map(function (id) {
    return { id: id, label: PALETTES[id].label, swatch: PALETTES[id].swatch };
  });

  var readyPromise = null;
  function ready() {
    if (readyPromise) return readyPromise;
    if (!document.fonts || !document.fonts.load) {
      readyPromise = Promise.resolve();
      return readyPromise;
    }
    readyPromise = Promise.all([
      document.fonts.load('100px "' + SCRIPT + '"', 'Bride'),
      document.fonts.load('700 100px "' + CAPS + '"', 'SQUAD'),
    ]).then(
      function () {},
      function () {}
    );
    return readyPromise;
  }

  // ---------- geometry helpers ----------

  // Concave-corner ("wedding card") rectangle.
  function framePath(ctx, x0, y0, x1, y1, r) {
    ctx.beginPath();
    ctx.moveTo(x0 + r, y0);
    ctx.lineTo(x1 - r, y0);
    ctx.arc(x1, y0, r, Math.PI, Math.PI / 2, true);
    ctx.lineTo(x1, y1 - r);
    ctx.arc(x1, y1, r, -Math.PI / 2, -Math.PI, true);
    ctx.lineTo(x0 + r, y1);
    ctx.arc(x0, y1, r, 0, -Math.PI / 2, true);
    ctx.lineTo(x0, y0 + r);
    ctx.arc(x0, y0, r, Math.PI / 2, 0, true);
    ctx.closePath();
  }

  function hexRgb(h) {
    var n = parseInt(h.slice(1), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }

  function vGradient(ctx, yTop, yBottom, stops) {
    var g = ctx.createLinearGradient(0, yTop, 0, yBottom);
    var n = stops.length - 1;
    stops.forEach(function (c, i) {
      g.addColorStop(i / n, c);
    });
    return g;
  }

  function leaf(ctx, cx, cy, len, angle) {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    var g = ctx.createLinearGradient(0, -len * 0.25, 0, len * 0.25);
    g.addColorStop(0, LEAF[0]);
    g.addColorStop(1, LEAF[1]);
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.quadraticCurveTo(len * 0.5, -len * 0.32, len, 0);
    ctx.quadraticCurveTo(len * 0.5, len * 0.32, 0, 0);
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.35)';
    ctx.lineWidth = len * 0.03;
    ctx.beginPath();
    ctx.moveTo(len * 0.08, 0);
    ctx.lineTo(len * 0.88, 0);
    ctx.stroke();
    ctx.restore();
  }

  // Layered-petal marigold (genda phool).
  function marigold(ctx, cx, cy, R, pal) {
    var rings = [
      { k: 1.0, n: 18, c: pal.petals[0] },
      { k: 0.78, n: 15, c: pal.petals[1] },
      { k: 0.56, n: 11, c: pal.petals[2] },
    ];
    rings.forEach(function (ring, ri) {
      var dist = R * ring.k * 0.66;
      var pr = R * ring.k * 0.36;
      for (var i = 0; i < ring.n; i++) {
        var a = (i / ring.n) * Math.PI * 2 + ri * 0.35;
        var px = cx + Math.cos(a) * dist;
        var py = cy + Math.sin(a) * dist;
        ctx.beginPath();
        ctx.arc(px, py, pr, 0, Math.PI * 2);
        ctx.fillStyle = ring.c;
        ctx.fill();
        ctx.lineWidth = Math.max(1, R * 0.03);
        ctx.strokeStyle = pal.line;
        ctx.stroke();
      }
    });
    ctx.beginPath();
    ctx.arc(cx, cy, R * 0.22, 0, Math.PI * 2);
    ctx.fillStyle = pal.centre;
    ctx.fill();
    // tiny highlight dots on the centre
    ctx.fillStyle = 'rgba(255,220,120,0.6)';
    for (var d = 0; d < 6; d++) {
      var da = (d / 6) * Math.PI * 2;
      ctx.beginPath();
      ctx.arc(cx + Math.cos(da) * R * 0.11, cy + Math.sin(da) * R * 0.11, R * 0.03, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  function diamond(ctx, cx, cy, r) {
    ctx.beginPath();
    ctx.moveTo(cx, cy - r);
    ctx.lineTo(cx + r, cy);
    ctx.lineTo(cx, cy + r);
    ctx.lineTo(cx - r, cy);
    ctx.closePath();
    ctx.fill();
  }

  // Gold rule with a centre diamond and two dots.
  function divider(ctx, cy, halfW, fill) {
    ctx.fillStyle = fill;
    ctx.fillRect(500 - halfW, cy - 2, halfW - 40, 4);
    ctx.fillRect(540, cy - 2, halfW - 40, 4);
    diamond(ctx, 500, cy, 14);
    ctx.beginPath();
    ctx.arc(500 - halfW - 12, cy, 5, 0, Math.PI * 2);
    ctx.arc(500 + halfW + 12, cy, 5, 0, Math.PI * 2);
    ctx.fill();
  }

  // ---------- text helpers ----------

  function fontFor(style, px) {
    return style === 'caps' ? '700 ' + px + 'px "' + CAPS + '", Georgia, serif' : px + 'px "' + SCRIPT + '", cursive';
  }

  // Tracked text drawn glyph by glyph (ctx.letterSpacing is not in every
  // browser we serve). Returns metrics so the caller can lay it out.
  function measureLine(ctx, text, style, px, trackEm) {
    ctx.font = fontFor(style, px);
    var track = style === 'caps' ? px * (trackEm == null ? 0.22 : trackEm) : 0;
    var width = 0;
    var chars = Array.from(text);
    var advances = chars.map(function (ch) {
      var w = ctx.measureText(ch).width;
      return w;
    });
    if (track) {
      width = advances.reduce(function (a, b) {
        return a + b;
      }, 0) + track * (chars.length - 1);
    } else {
      width = ctx.measureText(text).width;
    }
    var m = ctx.measureText(text);
    var asc = m.actualBoundingBoxAscent || px * 0.75;
    var desc = m.actualBoundingBoxDescent || px * 0.2;
    return { text: text, style: style, px: px, track: track, chars: chars, advances: advances, width: width, asc: asc, desc: desc };
  }

  function drawLine(ctx, line, cx, baseline) {
    ctx.font = fontFor(line.style, line.px);
    ctx.textBaseline = 'alphabetic';
    if (!line.track) {
      ctx.textAlign = 'center';
      ctx.fillText(line.text, cx, baseline);
      return;
    }
    ctx.textAlign = 'left';
    var x = cx - line.width / 2;
    line.chars.forEach(function (ch, i) {
      ctx.fillText(ch, x, baseline);
      x += line.advances[i] + line.track;
    });
  }

  // Lay out a lockup (1-2 lines) to fit inside a box; returns positioned
  // lines. Script lines are big, caps lines are small and tracked; the
  // whole stack is uniformly scaled down if it overflows the box.
  function layoutLockup(ctx, lockup, box, sizes) {
    var lines = lockup.lines.map(function (ln) {
      var px = ln.s === 'script' ? sizes.script : sizes.caps;
      return measureLine(ctx, ln.t, ln.s, px);
    });
    var gap = sizes.gap;
    // a caps line tucked under a script line needs breathing room
    if (lockup.lines.length > 1 && lockup.lines[0].s === 'script') gap = sizes.gap + 14;
    var stackH = lines.reduce(function (acc, l) {
      return acc + l.asc + l.desc;
    }, 0) + gap * (lines.length - 1);
    var maxW = Math.max.apply(
      null,
      lines.map(function (l) {
        return l.width;
      })
    );
    var k = Math.min(1, box.w / maxW, box.h / stackH);
    if (k < 1) {
      lines = lockup.lines.map(function (ln, i) {
        return measureLine(ctx, ln.t, ln.s, Math.floor(lines[i].px * k));
      });
      gap = gap * k;
      stackH = lines.reduce(function (acc, l) {
        return acc + l.asc + l.desc;
      }, 0) + gap * (lines.length - 1);
    }
    var y = box.cy - stackH / 2;
    var placed = [];
    lines.forEach(function (l) {
      placed.push({ line: l, baseline: y + l.asc });
      y += l.asc + l.desc + gap;
    });
    return { placed: placed, top: box.cy - stackH / 2, bottom: box.cy + stackH / 2 };
  }

  function fitName(ctx, name, maxW, maxPx, minPx) {
    var px = maxPx;
    // Lighter tracking than the phrase caps line so long names stay big.
    var line = measureLine(ctx, name, 'caps', px, 0.12);
    if (line.width > maxW) {
      px = Math.max(minPx, Math.floor(px * (maxW / line.width)));
      line = measureLine(ctx, name, 'caps', px, 0.12);
    }
    // name tracking is lighter than the phrase caps line
    return line;
  }

  // ---------- main render ----------

  function render(canvas, opts) {
    var widthPx = Math.round(opts.widthPx || 1000);
    var heightPx = Math.round(widthPx * ASPECT);
    if (canvas.width !== widthPx) canvas.width = widthPx;
    if (canvas.height !== heightPx) canvas.height = heightPx;
    var ctx = canvas.getContext('2d');
    var s = widthPx / BASE_W;
    var pal = PALETTES[opts.colourway] || PALETTES.maroon;
    var special = !!opts.special;
    var name = (opts.name || '').trim().toUpperCase();

    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, widthPx, heightPx);
    ctx.scale(s, s);

    var x0 = 34,
      y0 = 74,
      x1 = 966,
      y1 = BASE_H - 34,
      r = 46;

    // Body
    framePath(ctx, x0, y0, x1, y1, r);
    // Radial glow drawn as stepped concentric bands instead of a smooth
    // gradient: looks the same at print size but keeps the PNG small
    // (a smooth gradient made a 10-inch file ~5MB, too heavy to upload
    // once per name on mobile data).
    ctx.fillStyle = pal.bg[1];
    ctx.fill();
    ctx.save();
    framePath(ctx, x0, y0, x1, y1, r);
    ctx.clip();
    var c0 = hexRgb(pal.bg[0]),
      c1 = hexRgb(pal.bg[1]),
      BANDS = 28,
      cyMid = (y0 + y1) / 2;
    for (var bi = BANDS - 1; bi >= 0; bi--) {
      var t = bi / (BANDS - 1);
      var rad = 60 + t * 520;
      ctx.beginPath();
      ctx.ellipse(500, cyMid, rad * 1.25, rad * 0.9, 0, 0, Math.PI * 2);
      ctx.fillStyle =
        'rgb(' +
        Math.round(c0[0] + (c1[0] - c0[0]) * t) +
        ',' +
        Math.round(c0[1] + (c1[1] - c0[1]) * t) +
        ',' +
        Math.round(c0[2] + (c1[2] - c0[2]) * t) +
        ')';
      ctx.fill();
    }
    ctx.restore();

    // Subtle jaali dot texture inside the body
    ctx.save();
    framePath(ctx, x0, y0, x1, y1, r);
    ctx.clip();
    ctx.fillStyle = 'rgba(255,255,255,0.06)';
    for (var gy = y0 + 20; gy < y1; gy += 36) {
      for (var gx = x0 + ((gy / 36) % 2 ? 18 : 0); gx < x1; gx += 36) {
        ctx.beginPath();
        ctx.arc(gx, gy, 2.2, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.restore();

    // Double border
    var frameGrad = vGradient(ctx, y0, y1, pal.frame);
    framePath(ctx, x0, y0, x1, y1, r);
    ctx.lineWidth = 12;
    ctx.strokeStyle = frameGrad;
    ctx.stroke();
    framePath(ctx, x0 + 24, y0 + 24, x1 - 24, y1 - 24, r);
    ctx.lineWidth = 3.5;
    ctx.stroke();

    // Corner marigolds
    [
      [x0, y0],
      [x1, y0],
      [x0, y1],
      [x1, y1],
    ].forEach(function (p) {
      marigold(ctx, p[0], p[1], 30, pal.marigold);
    });

    // Crest: leaves + marigold(s) on the top edge
    if (special) {
      leaf(ctx, 500, y0, 150, Math.PI + 0.12);
      leaf(ctx, 500, y0, 150, -0.12);
      marigold(ctx, 392, y0 + 2, 40, pal.marigold);
      marigold(ctx, 608, y0 + 2, 40, pal.marigold);
      marigold(ctx, 500, y0 - 6, 58, pal.marigold);
    } else {
      leaf(ctx, 500, y0, 110, Math.PI + 0.18);
      leaf(ctx, 500, y0, 110, -0.18);
      marigold(ctx, 500, y0 - 4, 52, pal.marigold);
    }

    var textGrad = vGradient(ctx, 120, BASE_H - 80, pal.text);
    var nameGrad;
    ctx.shadowColor = pal.shadow;
    ctx.shadowBlur = 6;
    ctx.shadowOffsetY = 3;

    var lockup = opts.lockup;
    var hasName = !!name;
    var lockBox, layout;
    if (!special) {
      lockBox = { cy: 262, w: 760, h: 270 };
      layout = layoutLockup(ctx, lockup, lockBox, { script: 176, caps: 66, gap: 4 });
    } else if (hasName) {
      lockBox = { cy: 282, w: 780, h: 290 };
      layout = layoutLockup(ctx, lockup, lockBox, { script: 210, caps: 70, gap: 4 });
    } else {
      lockBox = { cy: 380, w: 800, h: 430 };
      layout = layoutLockup(ctx, lockup, lockBox, { script: 280, caps: 86, gap: 6 });
    }
    ctx.fillStyle = textGrad;
    layout.placed.forEach(function (p) {
      drawLine(ctx, p.line, 500, p.baseline);
    });

    ctx.shadowColor = 'transparent';
    var divY = !special ? 440 : hasName ? 494 : BASE_H - 108;
    divider(ctx, divY, special ? 230 : 260, textGrad);
    ctx.shadowColor = pal.shadow;

    if (hasName) {
      var nameMax = special ? 108 : 138;
      var line = fitName(ctx, name, 790, nameMax, 48);
      var nameCy = special ? 584 : 556;
      var capH = line.asc;
      nameGrad = vGradient(ctx, nameCy - capH / 2, nameCy + capH / 2, pal.text);
      ctx.fillStyle = nameGrad;
      if (opts.placeholder) ctx.globalAlpha = 0.45;
      drawLine(ctx, line, 500, nameCy + capH / 2);
      ctx.globalAlpha = 1;
    }

    ctx.restore();
    return canvas;
  }

  function toBlob(canvas) {
    return new Promise(function (resolve) {
      canvas.toBlob(function (b) {
        resolve(b);
      }, 'image/png');
    });
  }

  window.CXSquad = {
    PHRASES: PHRASES,
    COUPLE_TITLES: COUPLE_TITLES,
    COLOURWAYS: COLOURWAYS,
    ASPECT: ASPECT,
    ready: ready,
    render: render,
    toBlob: toBlob,
    // used by the cover / sample image generator only
    drawMarigold: function (ctx, cx, cy, R, colourway) {
      marigold(ctx, cx, cy, R, (PALETTES[colourway] || PALETTES.maroon).marigold);
    },
    drawLeaf: leaf,
  };
})();
